import _ from 'lodash';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch, Link } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { QueryClientProvider } from 'react-query';
import ApiService from '../../services/ApiService';
import { useLocalStorage } from '../../util/hooks';
import DealContext from './DealContext';
import AddNote from './AddNote';
import MenuCrumb from '../MenuCrumb';
import FavoriteIcon from '../favorites/FavoriteIcon';
import { modal } from '../modal/ModalEmitter';
import toastr from '../toastr';
import { getSalesRoles } from './util';
import FeatureDisabled from './FeatureDisabled';
import SalesQueryClient from './SalesQueryClient';
import AddTab from './AddTab';
import QuoteTabs from './QuoteTabs';

const valuesForAggregation = [
  'addonTotal',
  'feesTotal',
  'grossProfit',
  'retailPrice',
  'salesTaxAmount',
  'warrantyTotal',
  'dueAtDelivery',
  'totalCosts',
];

const DealExplorer = ({ dealId, query }) => {
  const match = useRouteMatch('/db/EQS2/:dealId/edit/:area?');

  const [activeTabIndex, setActiveTabIndex] = useState(match.params.area || 'home');
  const [lastTabIndex, setLastTabIndex] = useState('home');

  const [ready, setReady] = useState(false);
  const [deal, setDeal] = useState({});
  const [notes, setNotes] = useState([]);
  const [states, setStates] = useState(null);
  const [quoteHistory, setQuoteHistory] = useState([]);
  const [quoteList, setQuoteList] = useState([]);
  const [quoteLayout, setQuoteLayout] = useState([]);
  const [grossProfit, setGrossProfit] = useState('');
  const [aggregateValues, setAggregateValues] = useState({});
  const [team, setTeam] = useState({});
  const [quoteMeta, setQuoteMeta] = useState({});
  const history = useHistory();

  const [openQuotes, setOpenQuotes] = useLocalStorage(`openQuotesDeal${dealId}`, []);
  const [showCustomer, setShowCustomer] = useLocalStorage(`showCustomer${dealId}`, false);

  const { user, config } = useSelector(state => state.app);
  const { users } = useSelector(state => state.chat);

  const { isSalesPerson, isSalesManager, isSalesAccountant } = getSalesRoles(user);

  const hasQuotes = quoteList.length > 0;
  const hasCustomer = !!deal.Customer;
  const viewingQuote = /^[0-9]+$/.test(activeTabIndex);

  const isOnTeam = isSalesManager || isSalesAccountant || (isSalesPerson && team[user.id] !== undefined);

  // TODO Cache or set to global storage?
  const fetchStates = () => {
    return ApiService.get('crm/states').then(response => {
      setStates(_.orderBy(response.data, 'order'));
    });
  };

  const fetchQuoteMeta = () => {
    return ApiService.get('crm/quote-meta').then(response => {
      setQuoteMeta(response.data);
    });
  };

  const fetchDeal = (params = {}) => {
    const { forceUpdate, clearCache } = params;
    return ApiService.get(`crm/deal/${dealId}`).then(response => {
      const { quotes, ...restOfDeal } = response.data;

      // Check for changes
      if (forceUpdate || restOfDeal.updatedAt !== deal.updatedAt) {
        setDeal(restOfDeal);
      }

      fetchQuoteHistory();

      // Check for any major changes to quote list (length)
      if (quoteList.length !== quotes.length) {
        setQuoteList(quotes);
        return;
      }

      // Check for any major changes to quote list (length same but ids diff)
      if (
        quotes
          .map(q => q.id)
          .sort()
          .join(':') !==
        quoteList
          .map(q => q.id)
          .sort()
          .join(':')
      ) {
        setQuoteList(quotes);
      }

      const newQuoteList = quoteList.map(q => {
        const quote = quotes.find(e => e.id === q.id);
        if (q.updatedAt !== quote.updatedAt) {
          quote.key = q.key;
          if (clearCache) {
            quote.key = q.key ? q.key + 1 : 1;
          }
          return quote;
        }

        return q;
      });

      setQuoteList(newQuoteList);
    });
  };

  const fetchDealNotes = () => {
    ApiService.get(`crm/deal/${dealId}/notes`)
      .then(response => {
        setNotes(response.data);
      })
      .catch(() => {
        toastr.error('Error', 'Fetching notes failed');
      });
  };

  const changeTab = tabKey => {
    if (String(tabKey) !== activeTabIndex) {
      setLastTabIndex(String(activeTabIndex));
      setActiveTabIndex(String(tabKey));
    }
  };

  const presentQuote = quoteId => {
    setOpenQuotes([...new Set([Number(quoteId), ...openQuotes])]);
    changeTab(quoteId);
  };

  const presentCustomer = customerId => {
    if (!deal.customerId || customerId !== deal.customerId) {
      fetchDeal().then(() => {
        setShowCustomer(true);
        changeTab('customer');
      });

      return;
    }

    setShowCustomer(true);
    changeTab('customer');
  };

  const handleNewQuote = () => {
    modal.open('quickCreate', {
      presets: {
        customer: deal.customerId,
        assignee: user.id,
        deal: deal.id,
      },
      submitCallback: (response, payload) => {
        if (payload && payload.continue === 'Y') {
          fetchDeal().then(() => {
            presentQuote(String(response.data.id));
          });
        } else {
          fetchDeal();
        }
      },
    });
  };

  const handleCopyQuote = () => {
    const quote = quoteList.find(q => q.id === Number(activeTabIndex));
    modal.open('copyQuote', {
      quote,
      submitCallback: fetchDeal,
    });
  };

  const handleCreateSimilar = () => {
    const quote = quoteList.find(q => q.id === Number(activeTabIndex));
    modal.open('createSimilarQuote', {
      quote,
      submitCallback: fetchDeal,
    });
  };

  const handleNewNote = () => {
    const activeQuote = /\d+/.test(activeTabIndex) ? activeTabIndex : '';

    modal.open('addNote', {
      title: 'Add Note',
      style: 'center',
      size: 'md',
      hideFooter: true,
      hidePrint: true,
      message: (
        <AddNote
          itemId={activeQuote}
          listId="EQS1"
          options={quoteList.map(q => ({ label: `Quote ${q.id}`, value: String(q.id) }))}
          submitCallback={() => {
            modal.close();
            fetchDealNotes();
          }}
        />
      ),
    });
  };

  const closeQuote = quoteId => {
    setOpenQuotes(openQuotes.filter(q => q !== quoteId));

    if (String(quoteId) === lastTabIndex) {
      setLastTabIndex('home');
    }

    if (activeTabIndex === String(quoteId)) {
      setActiveTabIndex(String(lastTabIndex));
    }
  };

  const closeCustomer = () => {
    setShowCustomer(false);

    if (activeTabIndex === 'customer') {
      setActiveTabIndex(String(lastTabIndex));
    }
  };

  const fetchQuoteLayout = () => {
    return ApiService.get('/db/EQS1/describe').then(response => {
      setQuoteLayout(response.data.meta.pageLayout);
    });
  };

  const fetchQuoteHistory = () => {
    return ApiService.get(`crm/deal/${dealId}/history`).then(response => {
      setQuoteHistory(response.data);
    });
  };

  const makeCalculations = () => {
    let profit = 0;

    if (!quoteList.length) {
      return;
    }

    const result = {};

    quoteList.forEach(quote => {
      if (!quote.data) {
        return;
      }

      valuesForAggregation.forEach(key => {
        if (!result[key]) {
          result[key] = 0;
        }

        if (quote.data[key]) {
          result[key] += parseFloat(quote.data[key]);
        }
      });

      if (quote.data.grossProfit) {
        profit += parseFloat(quote.data.grossProfit);
      }
    });

    setGrossProfit(String(profit));
    setAggregateValues(result);
  };

  const updateName = name => {
    return ApiService.put(`db/EQS2/${deal.id}`, { name }).then(() => {
      setDeal({ ...deal, name });
    });
  };

  const updateQuoteStatus = (quoteId, options) => {
    fetchDeal(options);
  };

  const refreshTeam = () => {
    // Assignees on Quotes
    const assignees = {};

    // Users receiving commission on quotes under deal
    const dealCommissionUsers = [];

    if (quoteList.length) {
      quoteList.forEach(quote => {
        const commissionUsers = _.get(quote, 'data.commission.user');

        if (commissionUsers && commissionUsers.length) {
          commissionUsers.forEach(commissionUserId => {
            if (!dealCommissionUsers.includes(commissionUserId)) {
              dealCommissionUsers.push(commissionUserId);
            }
          });
        }

        if (!assignees[quote.assignedTo]) {
          if (!quote.assignedTo) {
            assignees[quote.assignedTo] = { firstName: 'Unassigned', count: 1 };
          } else {
            assignees[quote.assignedTo] = { ...quote.AssignedTo, count: 1 };
          }
        } else {
          assignees[quote.assignedTo].count += 1;
        }
      });
    }

    dealCommissionUsers.forEach(userId => {
      const user = users.find(u => u.id === userId);
      if (user && !assignees[userId]) {
        assignees[userId] = { ...user, count: 0 };
      }
    });

    setTeam(assignees);
  };

  useEffect(() => {
    if (activeTabIndex) {
      if (!match.params.area) {
        history.replace(`edit/${activeTabIndex}`);
      } else {
        history.replace(activeTabIndex);
      }
    }
  }, [activeTabIndex]);

  useEffect(() => {
    if (quoteLayout && quoteLayout.length) {
      makeCalculations();
    }
  }, [quoteLayout]);

  useEffect(() => {
    makeCalculations();
  }, [openQuotes, quoteList]);

  // useEffect(() => {
  //   // Tab min-width 150 w/o Unit ~40
  //   const dealTabs = window.document.querySelector('.deal-tabs');
  //   if (dealTabs) {
  //     console.log(dealTabs.scrollWidth, dealTabs.clientWidth);
  //     if (dealTabs.scrollWidth > dealTabs.clientWidth) {
  //     }
  //   }
  // }, [openQuotes]);

  useEffect(() => {
    refreshTeam();

    if (ready) {
      // Check openQuotes to see if they make sense with what's in the list
      setOpenQuotes(current => {
        return current.filter(quoteId => {
          return quoteList.find(q => q.id === quoteId);
        });
      });
    }
  }, [quoteList]);

  useEffect(() => {
    if (query && query.oq) {
      setTimeout(() => {
        presentQuote(query.oq);
        history.replace({ search: null });
      });
    }
  }, [query]);

  useLayoutEffect(() => {
    if (match.params.area && /\d+/.test(match.params.area)) {
      presentQuote(match.params.area);
    }
  }, []);

  useEffect(() => {
    if (config.features.equipment) {
      Promise.all([fetchDeal(), fetchQuoteLayout(), fetchDealNotes(), fetchStates(), fetchQuoteMeta()]).then(values => {
        setReady(true);
      });
    }
  }, []);

  useEffect(() => {
    // Confirm actively viewed quote is in the loaded list
    if (ready) {
      if (/^\d+$/.test(activeTabIndex)) {
        const openQuote = openQuotes.includes(Number(activeTabIndex));
        if (!openQuote) {
          if (/^\d+$/.test(lastTabIndex)) {
            if (openQuotes.includes(Number(activeTabIndex))) {
              changeTab(lastTabIndex);
            } else {
              changeTab('home');
            }
          } else {
            changeTab(lastTabIndex);
          }
        }
      }
    }
  });

  if (!config.features.equipment) {
    return <FeatureDisabled />;
  }

  if (!ready) {
    return null;
  }

  const roles = {
    isSalesAccountant,
    isSalesPerson,
    isSalesManager,
    isOnTeam,
  };

  return (
    <QueryClientProvider client={SalesQueryClient}>
      <DealContext.Provider
        value={{
          activeTabIndex,
          aggregateValues,
          closeQuote,
          deal,
          fetchDeal,
          fetchDealNotes,
          grossProfit,
          handleCopyQuote,
          handleCreateSimilar,
          handleNewNote,
          handleNewQuote,
          notes,
          openQuotes,
          presentCustomer,
          presentQuote,
          quoteHistory,
          quoteList,
          quoteMeta,
          roles,
          states,
          team,
          updateName,
          updateQuoteStatus,
        }}
      >
        <div className="deal-explorer flex-grow-1 pr-3 pl-3">
          <div id="slim-title-container" style={{ marginLeft: '-15px', marginRight: '-15px' }}>
            <div className="page-title-items">
              <div className="page-title-wrapper">
                <FavoriteIcon pageName="EQS2" /> <span className="page-title">Viewing Deal</span>
                <div>
                  {isSalesManager && <i className="fas fa-crown ml-2" title="Manager" />}
                  {isSalesPerson && <i className="fas fa-user-tie ml-2" title="Salesperson" />}
                  {isSalesAccountant && <i className="fas fa-coin ml-2" title="Accountant" />}
                </div>
              </div>
              <div className="title-link">
                <Link to="/EQS1">Quotes</Link>
              </div>
              <div className="title-link ml-1">
                <Link to="/EQS7">Inventory</Link>
              </div>
              <MenuCrumb
                pageName="EQS2"
                itemId={deal.id}
                sectionName="Equipment"
                sectionColor="#673ab7"
                readable="Deals"
              />
            </div>
          </div>
          <Row className="explore-body">
            <Col className="deal-tab-container p-0">
              <QuoteTabs
                activeTabIndex={activeTabIndex}
                changeTab={changeTab}
                showCustomer={showCustomer}
                closeCustomer={closeCustomer}
                setActiveTabIndex={setActiveTabIndex}
              />
              <AddTab
                hasCustomer={hasCustomer}
                hasQuotes={hasQuotes}
                showCustomer={showCustomer}
                viewingQuote={viewingQuote}
              />
            </Col>
          </Row>
        </div>
      </DealContext.Provider>
    </QueryClientProvider>
  );
};

export default DealExplorer;
